import { evaluateCondition } from "./conditions.js";
import { deriveRoute } from "./branching.js";
import { calculateBaseScores, normalizeScores } from "./scoring.js";
export function calculateResult(quiz, answers) {
    const rawScores = calculateBaseScores(quiz, answers);
    const adjustedScores = { ...rawScores };
    const matchedRules = [];
    const explanationTags = [];
    let overrideResultId;
    let eligibleResultIds = new Set(quiz.results.map((result) => result.id));
    for (const rule of quiz.scoring.rules ?? []) {
        if (!evaluateCondition(rule.when, { answers, scores: adjustedScores }))
            continue;
        matchedRules.push(rule.id);
        if (rule.reason)
            explanationTags.push(rule.reason);
        switch (rule.effect.type) {
            case "override":
                overrideResultId = rule.effect.resultId;
                break;
            case "restrict":
                {
                    const { resultIds } = rule.effect;
                    eligibleResultIds = new Set([...eligibleResultIds].filter((resultId) => resultIds.includes(resultId)));
                }
                break;
            case "boost":
                adjustedScores[rule.effect.category] = (adjustedScores[rule.effect.category] ?? 0) + rule.effect.value;
                break;
            case "reduce":
                adjustedScores[rule.effect.category] = (adjustedScores[rule.effect.category] ?? 0) - rule.effect.value;
                break;
            case "tag":
                explanationTags.push(rule.effect.tag);
                break;
            default:
                assertNever(rule.effect);
        }
        if (rule.stop)
            break;
    }
    const normalizedScores = normalizeScores(adjustedScores, quiz.scoring.normalizeTo ?? 100);
    const result = overrideResultId
        ? requireResult(quiz, overrideResultId)
        : chooseWinningResult(quiz, normalizedScores, eligibleResultIds);
    return {
        id: result.id,
        rawScores,
        adjustedScores,
        normalizedScores,
        matchedRules,
        explanationTags,
        score: normalizedScores[result.category] ?? 0
    };
}
export function calculateQuizResult(quiz, answers) {
    if (quiz.personalizedResult?.mode === "answer-observations") {
        return calculateAnswerObservationsResult(quiz, answers);
    }
    return calculateResult(quiz, answers);
}
export function calculateAnswerObservationsResult(quiz, answers) {
    if (quiz.personalizedResult?.mode !== "answer-observations") {
        throw new Error(`Quiz "${quiz.slug}" does not use answer observations`);
    }
    const route = deriveRoute(quiz, answers);
    const gender = quiz.personalizedResult.genderQuestionId ? answers[quiz.personalizedResult.genderQuestionId] : undefined;
    const observations = route.visitedQuestionIds.flatMap((questionId) => {
        const question = quiz.questions.find((candidate) => candidate.id === questionId);
        const answerId = answers[questionId];
        const answer = question?.answers.find((candidate) => candidate.id === answerId);
        if (!answer?.resultObservation)
            return [];
        return [{
                questionId,
                answerId,
                text: resolvePersonalizedText(answer.resultObservation, gender)
            }];
    });
    return {
        id: "answer-observations",
        mode: "answer-observations",
        observations,
        score: 0
    };
}
export function isAnswerObservationsResult(result) {
    return "mode" in result && result.mode === "answer-observations";
}
export function toClientResultSnapshot(result) {
    if (isAnswerObservationsResult(result)) {
        return {
            mode: "answer-observations",
            id: result.id,
            observations: result.observations.map((observation) => ({
                question_id: observation.questionId,
                answer_id: observation.answerId,
                text: observation.text
            }))
        };
    }
    return {
        mode: "scored",
        id: result.id,
        raw_scores: result.rawScores,
        normalized_scores: result.normalizedScores,
        matched_rules: result.matchedRules
    };
}
export function chooseWinningResult(quiz, normalizedScores, eligibleResultIds = new Set(quiz.results.map((result) => result.id))) {
    const eligibleResults = quiz.results.filter((result) => eligibleResultIds.has(result.id));
    if (eligibleResults.length === 0) {
        throw new Error(`No eligible results remain for quiz "${quiz.slug}"`);
    }
    const tieBreaker = quiz.scoring.tieBreaker ?? quiz.results.map((result) => result.id);
    return [...eligibleResults].sort((a, b) => {
        const scoreDelta = (normalizedScores[b.category] ?? 0) - (normalizedScores[a.category] ?? 0);
        if (scoreDelta !== 0)
            return scoreDelta;
        const priorityDelta = (b.priority ?? 0) - (a.priority ?? 0);
        if (priorityDelta !== 0)
            return priorityDelta;
        return tieBreaker.indexOf(a.id) - tieBreaker.indexOf(b.id);
    })[0];
}
function requireResult(quiz, resultId) {
    const result = quiz.results.find((candidate) => candidate.id === resultId);
    if (!result)
        throw new Error(`Unknown override result "${resultId}" in quiz "${quiz.slug}"`);
    return result;
}
function resolvePersonalizedText(text, gender) {
    if (typeof text === "string")
        return text;
    if (gender === "female" || gender === "woman")
        return text.female;
    if (gender === "male" || gender === "man")
        return text.male;
    return text.neutral ?? text.male;
}
function assertNever(value) {
    throw new Error(`Unsupported result effect: ${JSON.stringify(value)}`);
}
